import { buildCompositeId, type IngredientReferenceSource } from "src/api/client-api/ingredientReferenceUnified/types";
import type { ProductFormProductIngredient } from "src/pages/Product/ProductForm/ProductForm.types";

export type EcobalyseIngredientMassRatios = {
    nonEdiblePartRatio?: number | null;
    rawToCookedRatio?: number | null;
};

export type EcobalyseIngredientMassRatiosMap = ReadonlyMap<string, EcobalyseIngredientMassRatios>;

/** Mirrors {@see ProductMassCalculator::ECS_SCORE_ID} on platform-api. */
export const ECS_SCORE_ID = 11;

export const formatGramsForDisplay = (grams: number): number => Math.round(grams);

export const roundQuantityGrams = (grams: number): number => Math.round(grams * 100) / 100;

export const formatQuantityGrams = (grams: number): string => roundQuantityGrams(grams).toFixed(2);

export const computeEcsTransportScorePerEdibleKg = (absolutePts: number, totalEdibleMassG: number): number => {
    if (totalEdibleMassG <= 0) {
        return absolutePts;
    }

    return absolutePts / (totalEdibleMassG / 1000);
};

export const computeEdibleMassG = (usedQuantityG: number, ratios: EcobalyseIngredientMassRatios): number => {
    if (usedQuantityG <= 0) {
        return 0;
    }

    return roundQuantityGrams(usedQuantityG * (1 - (ratios.nonEdiblePartRatio ?? 0)));
};

export const getIngredientReferenceCompositeId = (productIngredient: ProductFormProductIngredient): string | null => {
    if (productIngredient.ingredientReference?.compositeId) {
        return productIngredient.ingredientReference.compositeId;
    }

    const referenceId =
        productIngredient.ingredientReference?.id ?? productIngredient.ingredient?.ingredientReferenceId;
    if (referenceId === undefined) {
        return null;
    }

    const referenceSource =
        productIngredient.ingredientReference?.source ?? productIngredient.ingredient?.referenceSource ?? "sure";

    return buildCompositeId(referenceSource as IngredientReferenceSource, referenceId);
};

export const hasNeutralMassRatios = (ratios: EcobalyseIngredientMassRatios): boolean =>
    (ratios.nonEdiblePartRatio ?? 0) === 0 && (ratios.rawToCookedRatio ?? null) === null;

export const resolveEdibleMassG = (
    productIngredient: ProductFormProductIngredient,
    ratiosByCompositeId?: EcobalyseIngredientMassRatiosMap,
): number => {
    const usedQuantityG = productIngredient.usedQuantity ?? 0;
    const ratios = resolveIngredientMassRatios(productIngredient, ratiosByCompositeId);
    const computedEdibleMassG = computeEdibleMassG(usedQuantityG, ratios);

    if (
        usedQuantityG > 0 &&
        hasNeutralMassRatios(ratios) &&
        getIngredientReferenceCompositeId(productIngredient) === null &&
        productIngredient.quantity !== null &&
        productIngredient.quantity !== undefined
    ) {
        return productIngredient.quantity;
    }

    return computedEdibleMassG;
};

export const resolveIngredientMassRatios = (
    productIngredient: ProductFormProductIngredient,
    ratiosByCompositeId?: EcobalyseIngredientMassRatiosMap,
): EcobalyseIngredientMassRatios => {
    const embeddedRatios: EcobalyseIngredientMassRatios = {
        nonEdiblePartRatio: productIngredient.ingredientReference?.nonEdiblePartRatio ?? null,
        rawToCookedRatio: productIngredient.ingredientReference?.rawToCookedRatio ?? null,
    };

    if (embeddedRatios.nonEdiblePartRatio !== null || embeddedRatios.rawToCookedRatio !== null) {
        return embeddedRatios;
    }

    const compositeId = getIngredientReferenceCompositeId(productIngredient);
    if (compositeId === null || ratiosByCompositeId === undefined) {
        return embeddedRatios;
    }

    return ratiosByCompositeId.get(compositeId) ?? embeddedRatios;
};

export const collectIngredientReferenceCompositeIds = (
    productIngredients: ProductFormProductIngredient[],
): string[] => {
    const compositeIds = new Set<string>();

    for (const productIngredient of productIngredients) {
        const compositeId = getIngredientReferenceCompositeId(productIngredient);
        if (compositeId !== null) {
            compositeIds.add(compositeId);
        }
    }

    return Array.from(compositeIds);
};
